import { useGetAiWorkersUsageByCategoryMutation, aiworkerUsageType } from "@circuitry-ai/doc-data";
import { useTranslation } from "react-i18next";
import { AgGridTable, ErrorBoundary, convertToLocalDayMonthYear } from "@circuitry-ai/doc-ui";
import { useState, useEffect, useRef, useCallback } from "react";
import { GridApi } from "@ag-grid-community/core";

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export interface AnalysthistoryProps { }

export function AnalystHistory(props: AnalysthistoryProps) {
  const { t } = useTranslation();
  const [getAiWorkersUsageByCategory, { isLoading, error }] = useGetAiWorkersUsageByCategoryMutation();
  const [analystHistory, setAnalystHistory] = useState<any[]>([]);
  const [paginationTotalRecords, setPaginationTotalRecords] = useState<number>(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const fetchInProgressRef = useRef<boolean>(false);
  const lastPaginationPageRef = useRef<number>(-1);

  const fetchAnalystHistory = useCallback(async (page = 1) => {
    if (fetchInProgressRef.current) return;
    
    fetchInProgressRef.current = true;
    try {
      const payload = {
        pagination: {
          page: page,
          per_page: 50
        },
        filters: [
          {
            aiworker_type: aiworkerUsageType.ANALYST,
            condition: "contains"
          }
        ]
      };

      const response = await getAiWorkersUsageByCategory(payload).unwrap();
      const conversations = response?.aiworkers_conversations ?? [];
      
      if (page === 1) {
        setAnalystHistory(conversations);
      } else {
        setAnalystHistory(prev => [...prev, ...conversations]);
      }
      
      if (response?.total != null) {
        setPaginationTotalRecords(response.total);
      }
    } catch (err) {
      console.error('Error fetching analyst history:', err);
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [getAiWorkersUsageByCategory]);

  useEffect(() => {
    fetchAnalystHistory(1);
  }, [fetchAnalystHistory]);

  const updateTablePaginationSummary = useCallback((api: GridApi) => {
    try {
      const rowCountElement = document.querySelector('[ref="lbRecordCount"]');
      const totalPagesElement = document.querySelector('[ref="lbTotal"]');
      const pageSize = api.paginationGetPageSize();
      const totalRecords = paginationTotalRecords;
      const totalPages = Math.ceil((totalRecords || 0) / (pageSize || 1));
      if (totalPagesElement) totalPagesElement.textContent = `${totalPages}`;
      if (rowCountElement) rowCountElement.textContent = `${totalRecords ?? 0}`;
    } catch { }
  }, [paginationTotalRecords]);

  const onPaginationChanged = useCallback((params: any) => {
    if (fetchInProgressRef.current) return;
    
    updateTablePaginationSummary(params.api);
    const { api } = params;
    const current = api.paginationGetCurrentPage();
    const rowCount = api.paginationGetRowCount();
    const pageSize = api.paginationGetPageSize();
    
    // Calculate how many records we need for the next page
    const recordsNeeded = (current + 1) * pageSize;
    
    // Fetch more data if we're running low on loaded records (when we need more than 80% of what we have)
    // and there are more records available from the API
    if (recordsNeeded > rowCount * 0.8 && rowCount < paginationTotalRecords) {
      // Calculate which API page we need (based on API page size of 50)
      const apiPageSize = 50;
      const nextApiPage = Math.floor(rowCount / apiPageSize) + 1;
      
      // Prevent duplicate requests for the same page
      if (lastPaginationPageRef.current === nextApiPage) {
        return;
      }
      
      lastPaginationPageRef.current = nextApiPage;
      fetchAnalystHistory(nextApiPage);
    } else {
      lastPaginationPageRef.current = -1;
    }
  }, [fetchAnalystHistory, paginationTotalRecords, updateTablePaginationSummary]);

  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = params.api as GridApi;
    params.api.hideOverlay();
  }, []);

  const columnDefs = [
    {
      headerName: `${t('Insights')}`,
      field: "aiworker_name",
      sortable: true,
      flex: 2
    },
    {
      headerName: `${t('Executed_by')}`,
      field: "user_name",
      sortable: true,
      maxWidth: 250
    },
    {
      headerName: `${t('Timestamp')}`,
      field: "date",
      sortable: true,
      maxWidth: 250,
      valueFormatter: (params: any) => {
        if (!params?.value) return '';
        return convertToLocalDayMonthYear(params.value);
      }
    }
  ];

  const AnalystTableContentWithLoadingAndError = ErrorBoundary(AnalystTableContent);

  return (
    <div className="flex flex-col h-full py-3 px-4">
      <AnalystTableContentWithLoadingAndError
        error={error}
        isLoading={isLoading}
        teamData={analystHistory}
        columnDefs={columnDefs}
        onPaginationChanged={onPaginationChanged}
        onGridReady={onGridReady}
      />
    </div>
  );
}

const AnalystTableContent = ({ teamData, columnDefs, onPaginationChanged, onGridReady }: any) => {
  return (
    <AgGridTable
      tableId="analyst-history-table"
      tableColumnDefs={columnDefs}
      tableData={teamData}
      onPaginationChanged={onPaginationChanged}
      onGridReady={onGridReady}
    />
  );
};

export default AnalystHistory;
